import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for authentication and logging
api.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config
    
    // If the error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      try {
        // Try to refresh the token
        const refreshResponse = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include'
        })
        
        if (!refreshResponse.ok) {
          throw new Error('Refresh failed')
        }
        
        const refreshData = await refreshResponse.json()
        
        const { access_token } = refreshData
        localStorage.setItem('access_token', access_token)
        
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`
        return api(originalRequest)
        
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }
    
    return Promise.reject(error)
  }
)

// API functions
export const shipmentAPI = {
  // Master AWB operations
  getMasterAwbs: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })
    return api.get(`/master-awbs/?${params.toString()}`)
  },

  // Get Master AWB by number
  getMasterAwbByNumber: (masterAwbNumber) => {
    return api.get(`/master-awbs/${masterAwbNumber}`)
  },

  // Get unique Master AWB numbers
  getUniqueMasterAwbs: () => {
    return api.get('/master-awbs-list/')
  },

  // Create Master AWB with Child AWBs and Shipments
  createMasterAwb: (masterAwbData) => {
    return api.post('/master-awbs/', masterAwbData)
  },

  // Get Child AWBs by Master AWB
  getChildAwbsByMaster: (masterAwbNumber) => {
    return api.get(`/child-awbs/${masterAwbNumber}`)
  },

  // Legacy shipment operations (still supported)
  // Get all shipments with filters
  getShipments: (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })
    return api.get(`/shipments/?${params.toString()}`)
  },

  // Get shipments by AWB number (deprecated - use getMasterAwbByNumber)
  getShipmentsByAwb: (awbNo) => {
    return api.get(`/master-awbs/${awbNo}`)
  },

  // Get shipments by order_id
  getShipmentsByOrderId: (orderId) => {
    return api.get(`/shipments/by-order/${orderId}`)
  },

  // Get unique AWB numbers (deprecated - use getUniqueMasterAwbs)
  getUniqueAwbs: () => {
    return api.get('/master-awbs-list/')
  },

  // Create shipments from CSV (deprecated - use createMasterAwb)
  createShipmentsFromCsv: (csvData) => {
    // Transform old format to new Master AWB format if needed
    return api.post('/master-awbs/', csvData)
  },

  // Update single shipment
  updateShipment: (shipmentId, updateData) => {
    return api.put(`/shipments/${shipmentId}`, updateData)
  },

  // Delete shipment
  deleteShipment: (shipmentId) => {
    return api.delete(`/shipments/${shipmentId}`)
  },


  // Update tracking information
  updateTracking: () => {
    return api.post('/update-tracking/')
  },

  // Update FedEx AWB tracking (Child AWBs)
  updateFedexTracking: () => {
    return api.post('/update-fedex-tracking/')
  },

  // Update latest delivery dates
  updateLatestDelivery: () => {
    return api.post('/update-latest-delivery/')
  },

  // Upload and parse CSV file  
  uploadCsv: (file, projectId) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('project_id', projectId)
    return api.post('/upload-csv/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // Process uploaded CSV data
  processCsv: (csvData) => {
    return api.post('/process-csv/', csvData)
  },
}

export default api